/**
 * Pipeline step body for CreateObjectTypeWizard — pipeline name + the
 * drag-and-drop stage list. Extracted from CreateObjectTypeWizard.tsx to
 * keep that file within the project's file-size convention.
 *
 * There is deliberately no on/off toggle: this step only renders when the
 * customer picked the kind that rides a pipeline, so "does this have a
 * pipeline?" is already answered and asking twice let a customer build a
 * staged object with no stages.
 */

import type { DragEndEvent, DragStartEvent, useSensors } from '@dnd-kit/core';
import { closestCenter, DndContext, DragOverlay } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { memo, useMemo } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { FormField } from '@/lib/base/forms/form-field';
import { InputBase } from '@/lib/base/forms/input/input';
import { InputGroup } from '@/lib/base/forms/input/input-group';
import { useWizardContext } from '@/lib/base/wizard';

import {
  SortableStageRow,
  type StageItem,
  StageRowPreview,
} from '../StageManager';

export interface PipelineStepProps {
  pipelineName: string;
  onPipelineNameChange: (value: string) => void;
  stages: StageItem[];
  activeStage: StageItem | null;
  validationErrors: Record<string, { name?: string }>;
  sensors: ReturnType<typeof useSensors>;
  onStageNameChange: (id: string, name: string) => void;
  onStageDelete: (id: string) => void;
  onStageAdd: () => void;
  onDragStart: (event: DragStartEvent) => void;
  onDragEnd: (event: DragEndEvent) => void;
  onDragCancel: () => void;
}

function PipelineStepBodyImpl({
  pipelineName,
  onPipelineNameChange,
  stages,
  activeStage,
  validationErrors,
  sensors,
  onStageNameChange,
  onStageDelete,
  onStageAdd,
  onDragStart,
  onDragEnd,
  onDragCancel,
}: PipelineStepProps) {
  const { isSubmitting } = useWizardContext();

  // Stable handler map so individual SortableStageRow components don't
  // see a new function reference every render unless their stage
  // membership changes.
  const stageHandlers = useMemo(
    () =>
      Object.fromEntries(
        stages.map(s => [
          s.id,
          {
            onNameChange: (name: string) => onStageNameChange(s.id, name),
            onRemove: () => onStageDelete(s.id),
          },
        ])
      ) as Record<
        string,
        { onNameChange: (name: string) => void; onRemove: () => void }
      >,
    [stages, onStageNameChange, onStageDelete]
  );

  return (
    <div className='space-y-4'>
      <FormField name='pipelineName' label='Pipeline name'>
        {({ isInvalid, showError }) => (
          <>
            <InputGroup
              aria-label='Pipeline name'
              value={pipelineName}
              onChange={onPipelineNameChange}
              isDisabled={isSubmitting}
              isInvalid={isInvalid}
              showError={showError}
            >
              <InputBase placeholder='e.g. Sales Pipeline' />
            </InputGroup>
            {!showError && (
              <p className='text-xs mt-1 text-[var(--color-text-tertiary)]'>
                Name for the default pipeline
              </p>
            )}
          </>
        )}
      </FormField>

      <div>
        <label className='block text-sm font-medium mb-2 text-[var(--color-text-primary)]'>
          Stages
        </label>

        <DndContext
          sensors={isSubmitting ? [] : sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          onDragCancel={onDragCancel}
        >
          <SortableContext
            items={stages.map(s => s.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className='space-y-3'>
              {stages.map(stage => {
                const { onNameChange, onRemove } = stageHandlers[stage.id];
                return (
                  <SortableStageRow
                    key={stage.id}
                    id={stage.id}
                    stage={stage}
                    onNameChange={onNameChange}
                    onRemove={onRemove}
                    disabled={isSubmitting}
                    canDelete={stages.length > 1}
                    nameError={validationErrors[stage.id]?.name}
                  />
                );
              })}
            </div>
          </SortableContext>

          <DragOverlay>
            {activeStage ? <StageRowPreview stage={activeStage} /> : null}
          </DragOverlay>
        </DndContext>

        <div className='mt-3'>
          <Button
            size='sm'
            color='secondary'
            onClick={onStageAdd}
            isDisabled={isSubmitting}
            disabledTooltip={isSubmitting ? 'Creating object type…' : undefined}
          >
            Add stage
          </Button>
        </div>
      </div>
    </div>
  );
}

export const PipelineStepBody = memo(PipelineStepBodyImpl);
